import type { FileNode, GitHubTreeEntry } from '@/types'

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? ''

// ─── Worker API Client ────────────────────────────────────────────────────────

export async function fetchRepoTree(
  owner:  string,
  repo:   string,
  branch: string
): Promise<{ tree: GitHubTreeEntry[]; sha: string; truncated: boolean }> {
  if (!WORKER_URL) throw new Error('VITE_WORKER_URL is not set in .env')

  const url = new URL(`${WORKER_URL}/api/tree`)
  url.searchParams.set('owner',  owner)
  url.searchParams.set('repo',   repo)
  url.searchParams.set('branch', branch)

  const res = await fetch(url.toString(), { cache: 'no-store' })
  const data = await res.json() as { error?: string; tree?: GitHubTreeEntry[]; sha?: string; truncated?: boolean }

  if (!res.ok) throw new Error(data.error ?? `Tree fetch failed (${res.status})`)

  return {
    tree:      data.tree      ?? [],
    sha:       data.sha       ?? '',
    truncated: data.truncated ?? false,
  }
}

export async function fetchFileContent(
  owner: string,
  repo:  string,
  sha:   string,
  path:  string
): Promise<{ content: string; sha: string; path: string }> {
  if (!WORKER_URL) throw new Error('VITE_WORKER_URL is not set in .env')

  const url = new URL(`${WORKER_URL}/api/file`)
  url.searchParams.set('owner', owner)
  url.searchParams.set('repo',  repo)
  url.searchParams.set('sha',   sha)
  url.searchParams.set('path',  path)

  const res = await fetch(url.toString(), { cache: 'no-store' })
  const data = await res.json() as { error?: string; content?: string; sha?: string; path?: string }

  if (!res.ok) throw new Error(data.error ?? `File fetch failed (${res.status})`)

  return {
    content: data.content ?? '',
    sha:     data.sha     ?? sha,
    path:    data.path    ?? path,
  }
}

// ─── Document Extractor ───────────────────────────────────────────────────────
// Returns a flat, sorted list of every .md / .mdx file in the tree.
// This is the primary surface for ExMarked's sidebar — no folder navigation.

export function extractMarkdownFiles(entries: GitHubTreeEntry[]): FileNode[] {
  return entries
    .filter((e) => e.type === 'blob' && /\.mdx?$/i.test(e.path))
    .map((e) => {
      const parts = e.path.split('/')
      return {
        name: parts[parts.length - 1],
        path: e.path,
        type: 'file' as const,
        sha:  e.sha,
      }
    })
    .sort((a, b) => a.path.localeCompare(b.path))
}

// ─── Directory Grouper ────────────────────────────────────────────────────────
// Groups a flat FileNode list by their parent directory path.
// Root-level files get the key ''.

export function groupByDirectory(files: FileNode[]): Map<string, FileNode[]> {
  const map = new Map<string, FileNode[]>()
  for (const file of files) {
    const parts  = file.path.split('/')
    const dirKey = parts.slice(0, -1).join('/')
    const bucket = map.get(dirKey) ?? []
    bucket.push(file)
    map.set(dirKey, bucket)
  }
  return map
}
